import { useSyncExternalStore } from "react";

/**
 * The smallest store that `useSyncExternalStore` needs.
 *
 * There is no state library here on purpose. The event scheduler is already an
 * external store — it owns a value and notifies on change — so adopting one
 * would mean wrapping the thing that matters in a second abstraction that does
 * the same job. Everything else in the app is small enough that this suffices.
 */
export interface Store<T> {
  get(): T;
  set(next: T | ((previous: T) => T)): void;
  subscribe(listener: () => void): () => void;
}

export function createStore<T>(initial: T): Store<T> {
  let value = initial;
  const listeners = new Set<() => void>();
  return {
    get: () => value,
    set(next) {
      const resolved = typeof next === "function" ? (next as (p: T) => T)(value) : next;
      if (Object.is(resolved, value)) return;
      value = resolved;
      for (const listener of [...listeners]) listener();
    },
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
  };
}

export function useStore<T>(store: Store<T>): T;
export function useStore<T, S>(store: Store<T>, select: (value: T) => S): S;
export function useStore<T, S>(store: Store<T>, select?: (value: T) => S): T | S {
  return useSyncExternalStore(
    store.subscribe,
    () => (select === undefined ? store.get() : select(store.get())),
    () => (select === undefined ? store.get() : select(store.get())),
  );
}
